import React, { useMemo } from 'react';

import { Select } from 'antd';

import styled from 'styled-components';

import { getDisplayValues, groupConversionValues } from '@libs/NwFeatures';

import { useFeaturesIndex } from '@hooks/API/UseFeatures';

const Option = Select.Option;

const Wrapper = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    padding-top: 6px;

    .selects-wrapper {
        display: flex;
        justify-content: flex-start;
        align-items: center;
        width: 100%;
        gap: 1rem;

        .ant-select {
            max-width: 14rem;
        }

        /* i due select del range non stanno affiancati su schermi stretti */
        @media (max-width: ${(props) => props.theme.NarrowScreenMaxWidth}) {
            flex-direction: column;
            align-items: stretch;

            .ant-select {
                max-width: none;
            }
        }
    }
`;

const FeaturesRangeFilter = ({ onChange, value, feature }) => {
    const featuresIndex = useFeaturesIndex();
    const conversion = featuresIndex.conversionById.get(feature.ConversionID);

    // una sola lista per le tendine e per la scelta del valore adiacente: chiave ed etichetta come la vede l'utente
    const options = useMemo(
        () =>
            groupConversionValues(conversion.ConversionValues).map((conversionValue) => ({
                key: String(conversionValue.Key),
                // senza fallback un'etichetta vuota farebbe mostrare ad antd il value dell'option,
                // cioè il Key interno della conversion
                label: getDisplayValues(conversionValue.DisplayValue, conversion.ID, featuresIndex, {
                    separator: ' • ',
                    fallbackToAllMetrics: true,
                }),
            })),
        [conversion, featuresIndex]
    );

    // il filtro è la coppia [min, max] e arriva dalle props: una posizione vuota significa "non ancora scelto".
    // Tenerne una copia in stato locale la faceva ripartire vuota a ogni riapertura del drawer, e il primo
    // valore già scelto veniva sovrascritto alla scelta del secondo
    const getSelectedKey = (position) => {
        const key = Array.isArray(value) ? value[position] : null;
        return key === null || key === undefined ? null : String(key);
    };
    const minKey = getSelectedKey(0);
    const maxKey = getSelectedKey(1);

    /**
     * Chiave adiacente con un'etichetta diversa da quella di partenza: su alcune metriche più chiavi mostrano
     * lo stesso valore, e fermarsi alla prima adiacente lascerebbe il range invariato agli occhi dell'utente.
     * Se da quel lato non c'è nessuna etichetta diversa resta la chiave di partenza.
     */
    const getAdjacentKey = (key, direction) => {
        const currentIndex = options.findIndex((option) => option.key === String(key));
        if (currentIndex < 0) {
            return String(key);
        }
        const step = direction === 'next' ? 1 : -1;
        const currentLabel = options[currentIndex].label;
        for (let index = currentIndex + step; index >= 0 && index < options.length; index += step) {
            if (options[index].label !== currentLabel) {
                return options[index].key;
            }
        }
        return String(key);
    };

    // l'altro estremo si sposta solo se manca o se il range sarebbe impossibile
    const handleChangeMin = (val) => {
        const nextMax = maxKey === null || Number(val) > Number(maxKey) ? getAdjacentKey(val, 'next') : maxKey;
        onChange([Number(val), Number(nextMax)]);
    };

    const handleChangeMax = (val) => {
        const nextMin = minKey === null || Number(val) < Number(minKey) ? getAdjacentKey(val, 'prev') : minKey;
        onChange([Number(nextMin), Number(val)]);
    };

    return (
        options.length > 0 && (
            <Wrapper>
                <div className="selects-wrapper">
                    <Select
                        autoComplete="off"
                        showSearch
                        //name={feature.Name}
                        //id={item.ID}
                        style={{ width: '100%' }}
                        value={minKey}
                        //disabled={disabled}
                        dropdownMatchSelectWidth={false}
                        onChange={handleChangeMin}
                        filterOption={(input, option) =>
                            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                        }
                        placeholder="Select value (min)"
                    >
                        {options.map((option, index) => {
                            return (
                                <Option key={`${option.key}${index}`} value={option.key}>
                                    {option.label}
                                </Option>
                            );
                        })}
                    </Select>
                    <Select
                        autoComplete="off"
                        showSearch
                        style={{ width: '100%' }}
                        value={maxKey}
                        onChange={handleChangeMax}
                        filterOption={(input, option) =>
                            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                        }
                        dropdownMatchSelectWidth={false}
                        placeholder="Select value (max)"
                    >
                        {options.map((option, index) => {
                            return (
                                <Option key={`${option.key}${index}`} value={option.key}>
                                    {option.label}
                                </Option>
                            );
                        })}
                    </Select>
                </div>
            </Wrapper>
        )
    );
};

export default FeaturesRangeFilter;
